import React, { useCallback, useEffect, useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import api from '../services/api';

interface Plano { id: number; nome: string; periodicidade: string; valor_base: number; ativo: boolean; }
interface Mensalidade { id: number; id_aluno: number; mes: number; ano: number; valor: number; aluno?: { nome?: string }; }
interface Boleto { id: number; id_mensalidade: number; vencimento: string; valor: number; status: string; }
interface Pagamento { id: number; id_boleto: number; valor_pago: number; data_pagamento: string; metodo_pagamento: string; }

interface Resumo {
  mesReferencia: string;
  recebidoMes: number;
  aReceber: number;
  vencidoValor: number;
  vencidoQtd: number;
  pagoQtd: number;
  pendenteQtd: number;
  inadimplenciaPct: number;
}
interface ItemInadimplencia {
  id: number; aluno_nome: string; aluno_id: number | null; vencimento: string; valor: number; dias_atraso: number;
}

const brl = (v: number) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBR = (s?: string) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');

function CardKpi({ titulo, valor, sub, cor }: { titulo: string; valor: string; sub?: string; cor: string }) {
  return (
    <div style={{ flex: '1 1 180px', minWidth: 180, background: '#fff', border: '1px solid #eee', borderLeft: `4px solid ${cor}`, borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#222', marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PainelFinanceiro() {
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [inad, setInad] = useState<ItemInadimplencia[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [r, i] = await Promise.all([
        api.get<Resumo>('/financeiro/resumo'),
        api.get<ItemInadimplencia[]>('/financeiro/inadimplencia'),
      ]);
      setResumo(r.data);
      setInad(Array.isArray(i.data) ? i.data : []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar o painel financeiro.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>💰 Painel Financeiro {resumo ? `— ${resumo.mesReferencia}` : ''}</h3>
        <button onClick={carregar} disabled={carregando} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}>
          {carregando ? 'Atualizando…' : '↻ Atualizar'}
        </button>
      </div>

      {erro && <div style={{ color: '#c62828', marginBottom: 10 }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <CardKpi titulo="Recebido no mês" valor={brl(resumo?.recebidoMes || 0)} cor="#2e7d32" />
        <CardKpi titulo="A receber" valor={brl(resumo?.aReceber || 0)} sub={`${resumo?.pendenteQtd || 0} pendentes`} cor="#1565c0" />
        <CardKpi titulo="Vencido" valor={brl(resumo?.vencidoValor || 0)} sub={`${resumo?.vencidoQtd || 0} boletos`} cor="#c62828" />
        <CardKpi titulo="Inadimplência" valor={`${resumo?.inadimplenciaPct ?? 0}%`} sub={`${resumo?.pagoQtd || 0} pagos`} cor="#e65100" />
      </div>

      <div style={{ marginTop: 18 }}>
        <h4 style={{ margin: '0 0 8px' }}>Inadimplência {inad.length > 0 ? `(${inad.length})` : ''}</h4>
        {inad.length === 0 ? (
          <div style={{ color: '#2e7d32', fontSize: 14 }}>✅ Nenhum boleto vencido. Tudo em dia!</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Criança</th>
                  <th style={{ padding: '8px 12px' }}>Vencimento</th>
                  <th style={{ padding: '8px 12px' }}>Valor</th>
                  <th style={{ padding: '8px 12px' }}>Atraso</th>
                </tr>
              </thead>
              <tbody>
                {inad.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 12px' }}>{r.aluno_nome}{r.aluno_id ? ` (#${r.aluno_id})` : ''}</td>
                    <td style={{ padding: '8px 12px' }}>{dataBR(r.vencimento)}</td>
                    <td style={{ padding: '8px 12px' }}>{brl(r.valor)}</td>
                    <td style={{ padding: '8px 12px', color: '#c62828', fontWeight: 700 }}>{r.dias_atraso} dia(s)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const badgeStatus = (status: string) => (
  <span className={`badge badge-${String(status).toLowerCase()}`}>{status}</span>
);

export default function FinanceiroPage() {
  return (
    <div className="stack-cadastros">
      <PainelFinanceiro />

      <TabelaCrud<Plano>
        titulo="Financeiro — Planos de Atendimento"
        endpoint="/financeiro/planos"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'nome', label: 'Plano' },
          { chave: 'periodicidade', label: 'Periodicidade' },
          { chave: 'valor_base', label: 'Valor base', render: (i) => brl(i.valor_base) },
          { chave: 'ativo', label: 'Ativo', render: (i) => (i.ativo ? 'Sim' : 'Não') },
        ]}
        itemVazio={{ nome: '', periodicidade: 'mensal', valor_base: 0, ativo: true }}
        validateItem={(i) => (!i.nome?.trim() ? 'Informe o nome do plano.' : (!i.valor_base || Number(i.valor_base) <= 0 ? 'Informe o valor base.' : null))}
        renderFormulario={(i, onChange) => (
          <>
            <label>Nome do plano<input value={i.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} /></label>
            <div className="linha-campos linha-3">
              <label>Periodicidade<select value={i.periodicidade ?? 'mensal'} onChange={(e) => onChange('periodicidade', e.target.value)}><option value="mensal">Mensal</option><option value="semestral">Semestral</option><option value="anual">Anual</option></select></label>
              <label>Valor base (R$)<input type="number" step="0.01" value={i.valor_base ?? 0} onChange={(e) => onChange('valor_base', Number(e.target.value))} /></label>
              <label><input type="checkbox" checked={i.ativo ?? true} onChange={(e) => onChange('ativo', e.target.checked)} /> Ativo</label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Mensalidade>
        titulo="Financeiro — Mensalidades"
        endpoint="/financeiro/mensalidades"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'id_aluno', label: 'Criança', render: (i) => i.aluno?.nome || `#${i.id_aluno}` },
          { chave: 'mes', label: 'Mês', render: (i) => `${String(i.mes).padStart(2, '0')}/${i.ano}` },
          { chave: 'valor', label: 'Valor', render: (i) => brl(i.valor) },
        ]}
        itemVazio={{ id_aluno: 0, mes: new Date().getMonth() + 1, ano: new Date().getFullYear(), valor: 0 }}
        validateItem={(i) => (!i.id_aluno ? 'Informe a criança (ID).' : (!i.valor || Number(i.valor) <= 0 ? 'Informe o valor.' : null))}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label>Criança ID<input type="number" value={i.id_aluno ?? 0} onChange={(e) => onChange('id_aluno', Number(e.target.value))} /></label>
              <label>Valor (R$)<input type="number" step="0.01" value={i.valor ?? 0} onChange={(e) => onChange('valor', Number(e.target.value))} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Mês<input type="number" min={1} max={12} value={i.mes ?? 1} onChange={(e) => onChange('mes', Number(e.target.value))} /></label>
              <label>Ano<input type="number" value={i.ano ?? new Date().getFullYear()} onChange={(e) => onChange('ano', Number(e.target.value))} /></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Boleto>
        titulo="Financeiro — Boletos / Cobranças"
        endpoint="/financeiro/boletos"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'id_mensalidade', label: 'Mensalidade' },
          { chave: 'vencimento', label: 'Vencimento', render: (i) => dataBR(i.vencimento) },
          { chave: 'valor', label: 'Valor', render: (i) => brl(i.valor) },
          { chave: 'status', label: 'Status', render: (i) => badgeStatus(i.status) },
        ]}
        itemVazio={{ id_mensalidade: 0, vencimento: '', valor: 0, status: 'pendente' }}
        validateItem={(i) => (!i.id_mensalidade ? 'Informe a mensalidade (ID).' : (!i.vencimento ? 'Informe o vencimento.' : (!i.valor || Number(i.valor) <= 0 ? 'Informe o valor.' : null)))}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label>Mensalidade ID<input type="number" value={i.id_mensalidade ?? 0} onChange={(e) => onChange('id_mensalidade', Number(e.target.value))} /></label>
              <label>Valor (R$)<input type="number" step="0.01" value={i.valor ?? 0} onChange={(e) => onChange('valor', Number(e.target.value))} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Vencimento<input type="date" value={i.vencimento ?? ''} onChange={(e) => onChange('vencimento', e.target.value)} /></label>
              <label>Status<select value={i.status ?? 'pendente'} onChange={(e) => onChange('status', e.target.value)}><option value="pendente">Pendente</option><option value="pago">Pago</option><option value="vencido">Vencido</option><option value="cancelado">Cancelado</option></select></label>
            </div>
          </>
        )}
      />

      <TabelaCrud<Pagamento>
        titulo="Financeiro — Pagamentos (baixa dá o boleto como pago)"
        endpoint="/financeiro/pagamentos"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'id_boleto', label: 'Boleto' },
          { chave: 'valor_pago', label: 'Valor pago', render: (i) => brl(i.valor_pago) },
          { chave: 'data_pagamento', label: 'Data', render: (i) => dataBR(i.data_pagamento) },
          { chave: 'metodo_pagamento', label: 'Método' },
        ]}
        itemVazio={{ id_boleto: 0, valor_pago: 0, data_pagamento: new Date().toISOString().slice(0, 10), metodo_pagamento: 'pix' }}
        validateItem={(i) => (!i.id_boleto ? 'Informe o boleto (ID).' : (!i.valor_pago || Number(i.valor_pago) <= 0 ? 'Informe o valor pago.' : (!i.data_pagamento ? 'Informe a data.' : null)))}
        renderFormulario={(i, onChange) => (
          <>
            <div className="linha-campos linha-2">
              <label>Boleto ID<input type="number" value={i.id_boleto ?? 0} onChange={(e) => onChange('id_boleto', Number(e.target.value))} /></label>
              <label>Valor pago (R$)<input type="number" step="0.01" value={i.valor_pago ?? 0} onChange={(e) => onChange('valor_pago', Number(e.target.value))} /></label>
            </div>
            <div className="linha-campos linha-2">
              <label>Data do pagamento<input type="date" value={i.data_pagamento ?? ''} onChange={(e) => onChange('data_pagamento', e.target.value)} /></label>
              <label>Método<select value={i.metodo_pagamento ?? 'pix'} onChange={(e) => onChange('metodo_pagamento', e.target.value)}><option value="pix">PIX</option><option value="cartao">Cartão</option><option value="dinheiro">Dinheiro</option><option value="transferencia">Transferência</option><option value="boleto">Boleto bancário</option></select></label>
            </div>
          </>
        )}
      />
    </div>
  );
}
